import React, { useRef, useState, FormEvent, ReactNode, ChangeEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { ApiError, getResponseErrors } from '../../http-client/Response';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useI18n } from '../../i18n';

interface FormComponentProps {
  children: ReactNode;
  onSubmit: (formData: Record<string, any>) => Promise<{ success: boolean; errors?: ApiError[] }>;
  initialData?: Record<string, any>;
  className?: string; // className for styling the form container
}

interface FieldErrors {
  [key: string]: string;
}

interface FieldInfo {
  name: string;
  required: boolean;
  type?: string;
}

// Only catches obvious typos; the server does the full check
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FormComponent: React.FC<FormComponentProps> = ({
  children,
  onSubmit,
  initialData = {},
  className,
}) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  // A ref blocks a second submit fired before the state update renders
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  // Inputs are usually wrapped in layout elements, so look through the whole
  // tree the same way processChildren does
  const collectFields = (nodes: ReactNode, fields: FieldInfo[] = []): FieldInfo[] => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;
      if (child.props.name) {
        fields.push({
          name: child.props.name as string,
          required: !!child.props.required,
          type: child.props.type,
        });
      } else if (child.props.children) {
        collectFields(child.props.children, fields);
      }
    });
    return fields;
  };
  const fields = collectFields(children);
  const fieldNames = fields.map((field) => field.name);

  // Done here instead of by the browser, whose messages follow the browser's
  // language rather than the one picked in the app
  const validateFields = (): FieldErrors => {
    const errors: FieldErrors = {};
    for (const field of fields) {
      const value = formData[field.name];
      const text = typeof value === 'string' ? value.trim() : value;
      if (field.required && !text) {
        errors[field.name] = t.common.forms.required;
      } else if (field.type === 'email' && text && !EMAIL_PATTERN.test(text)) {
        errors[field.name] = t.common.forms.invalidEmail;
      }
    }
    return errors;
  };

  const handleFieldChange = (name: string, value: any) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    // The user is fixing it, so the old message no longer applies
    if (fieldErrors[name]) {
      setFieldErrors((errors) => {
        const { [name]: _fixed, ...rest } = errors;
        return rest;
      });
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submittingRef.current) return;

    const invalid = validateFields();
    const invalidNames = Object.keys(invalid);
    if (invalidNames.length > 0) {
      setFieldErrors(invalid);
      setGeneralErrors([]);
      const first = e.currentTarget.elements.namedItem(invalidNames[0]);
      if (first instanceof HTMLElement) first.focus();
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralErrors([]);

    let response: { success: boolean; errors?: ApiError[] };
    try {
      response = await onSubmit(formData);
    } catch {
      response = { success: false };
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }

    if (!response.success) {
      let [errs, fieldErrs] = getResponseErrors(response.errors, t.common.forms)
      for (const field in fieldErrs) {
        if (!fieldNames.includes(field)) {
          // No input to show it next to, so show it at the top of the form instead
          errs.push(fieldErrs[field])
          delete fieldErrs[field]
        }
      }
      setFieldErrors(fieldErrs)
      setGeneralErrors(errs)
    }
  };

  // Recursively process children to find and enhance input elements
  const processChildren = (children: ReactNode): ReactNode => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return child;
      }

      // If this element has a name prop, it's a form field
      if (child.props.name) {
        const fieldName = child.props.name;
        const fieldError = fieldErrors[fieldName];
        const clonedField = React.cloneElement(child as React.ReactElement<any>, {
          onChange: (e: ChangeEvent<HTMLInputElement>) =>
            handleFieldChange(fieldName, e.target ? e.target.value : e),
          value: formData[fieldName] || '',
          className: fieldError
            ? `${child.props.className || ''} border-destructive`
            : child.props.className,
          'aria-invalid': fieldError ? 'true' : undefined,
          'aria-describedby': fieldError ? `${fieldName}-error` : undefined,
        });

        // Always wrapped, so showing or clearing the error doesn't re-mount the
        // input (it would lose focus while the user types)
        return (
          <div key={fieldName}>
            {clonedField}
            {fieldError && (
              <p id={`${fieldName}-error`} className="text-sm text-destructive mt-1">
                {fieldError}
              </p>
            )}
          </div>
        );
      }

      // Submit button: disabled with a spinner while the form is submitting
      if (child.props.type === "submit" || child.props.className === "form-submit") {
        return React.cloneElement(child as React.ReactElement<any>, {
          disabled: submitting || child.props.disabled,
          'aria-busy': submitting || undefined,
          children: submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {child.props.children}
            </>
          ) : (
            child.props.children
          ),
        });
      }

      // If this element has children, recursively process them
      if (child.props.children) {
        return React.cloneElement(child as React.ReactElement<any>, {
          children: processChildren(child.props.children),
        });
      }

      return child;
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={className}
      noValidate
    >
      {generalErrors && generalErrors.map((error, i) => (
        <Alert key={i} variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ))}

      {processChildren(children)}
    </form>
  );
};

export default FormComponent;
